import { identity } from "lodash";
import { createSelector } from "reselect";

import type { CrmFiltersModel } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-values";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { leadsFilterDtoToPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsFilterDtoAdapters";
import { createDeepEqualSelector, selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import {
    selectDynamicsExcludeAccountFields,
    selectDynamicsLastSynced,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import {
    selectHsExcludeAccountFields,
    selectHubspotLastSynced,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectSalesforceLastSynced,
    selectSfExcludeAccountFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import {
    selectAppliedSignalsGroupedByType,
    selectHasAppliedSignals,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

import type { ILeadsSearchFiltersDto, LeadsSearchFilterApiCategory } from "../types/leadsSearchFilters.dto";

import type { ILeadsSearchFiltersState } from "./types/state";
import {
    createAppPlatformFilterValue,
    createCountriesFilterValue,
    createDirtyFiltersFlag,
    createIsCrmApplied,
    createSavedSearchFiltersCount,
    getDirtyFilters,
    isCountriesWorldwide,
} from "./utils/selectCombiners";

const selectLeadsSearchFiltersState = createStatePropertySelector<ILeadsSearchFiltersState>(
    selectSalesIntelligenceState("leadsSearchFilters"),
);

const selectRawFiltersValue = selectLeadsSearchFiltersState("filters");

/** @deprecated Re-exports from advanced-search-filtering (panel state actions moved there) */
export {
    selectIsFiltersPanelExpanded,
    selectFilterMode,
    selectSearchQuery,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";

/*
 * Composed filter values selector for:
 * - Single source of truth for signals data (as it is domain-specific, not feature-specific)
 * - Simplified cross-slice coordination
 *
 * The leads-filters slice never persists `signalsFilters` (stripped at the reducer via
 * `withoutSignals`), so signals are derived solely from the signals slice here.
 * `signalsFilters` is omitted entirely when no signals are applied, so consumers
 * (save, search query, AI lead-gen) never send an empty signals collection.
 */
const selectComposedFiltersValue = createSelector(
    [selectRawFiltersValue, selectAppliedSignalsGroupedByType, selectHasAppliedSignals],
    (rawFilters, signalsDto, hasAppliedSignals): Partial<ILeadsSearchFiltersDto> =>
        hasAppliedSignals ? { ...rawFilters, signalsFilters: signalsDto } : rawFilters,
);
// BACKWARD COMPATIBILITY: Alias to composed selector
export const selectFiltersValue = selectComposedFiltersValue;

export const selectIsCrmApplied = (state: RootState, apiCategory: LeadsSearchFilterApiCategory | undefined): boolean =>
    createIsCrmApplied(selectComposedFiltersValue(state)[apiCategory!] as CrmFiltersModel | undefined);

export const selectFiltersValueDeepEqual = createDeepEqualSelector(selectComposedFiltersValue, identity) as unknown as (
    s: RootState,
) => Partial<ILeadsSearchFiltersDto>;

/**
 * Panel boundary (read): the leads DTO projected into flat, field-atomic panel space. Composite member
 * fields stay flat here; `useFiltersManager` owns folding them under the group instance key for rendering.
 */
export const selectFiltersPanelValue = createSelector(selectFiltersValueDeepEqual, leadsFilterDtoToPanelValue);

export const selectDirtyFilters = createSelector(selectFiltersValueDeepEqual, getDirtyFilters);
export const selectDirtyFiltersFlag = (state: RootState) => createDirtyFiltersFlag(selectDirtyFilters(state));

export const selectSavedSearchFiltersCount = (state: RootState) =>
    createSavedSearchFiltersCount(selectFiltersValueDeepEqual(state));

export const selectCountriesFilterValue = createSelector(selectFiltersValueDeepEqual, createCountriesFilterValue);
export const selectIsVisitsFromWorldwide = createSelector(selectCountriesFilterValue, isCountriesWorldwide);

export const selectAppPlatformFilterValue = createSelector(selectFiltersValueDeepEqual, createAppPlatformFilterValue);

export const selectIsExcludeByOpportunitiesSelected = createSelector(selectComposedFiltersValue, (filters) => {
    const staticListsToExclude = filters?.leadsFilters?.staticListsToExclude;
    return staticListsToExclude != undefined && staticListsToExclude.length > 0;
});

export const selectCrmFieldLabelByName = createSelector(
    selectSfExcludeAccountFields,
    selectHsExcludeAccountFields,
    selectDynamicsExcludeAccountFields,
    (_: RootState, props: [CrmIntegrationProvider, string[]]) => props,
    (sfFields, hsFields, dynamicsFields, [crm, fieldNames]) => {
        const fieldMap: Record<CrmIntegrationProvider, typeof sfFields> = {
            salesforce: sfFields,
            hubspot: hsFields,
            dynamics: dynamicsFields,
        };

        const selectedFields = fieldMap[crm] ?? [];

        return fieldNames.reduce<Record<string, string>>((acc, fieldName) => {
            const matchedField = selectedFields.find(({ name }) => name === fieldName);
            acc[fieldName] = matchedField?.label ?? fieldName;
            return acc;
        }, {});
    },
);

export const selectCrmLastSynced = createSelector(
    selectSalesforceLastSynced,
    selectHubspotLastSynced,
    selectDynamicsLastSynced,
    (_: RootState, props: CrmIntegrationProvider) => props,
    (salesforceLastSynced, hubspotLastSynced, dynamicsLastSynced, crm) => {
        const map: Record<CrmIntegrationProvider, typeof salesforceLastSynced> = {
            salesforce: salesforceLastSynced,
            hubspot: hubspotLastSynced,
            dynamics: dynamicsLastSynced,
        };

        return map[crm];
    },
);
